import DesignTokens from '@tableau/design-tokens';
import { FlowBaseIcon } from '@tableau/icons/FlowBaseIcon';
import { GotoGenericIcon } from '@tableau/icons/GotoGenericIcon';
import { NotificationInfoOutlineIcon } from '@tableau/icons/NotificationInfoOutlineIcon';
import { SiteUrls } from '@tableau/shared-urls';
import { CheckBoxWidget, Tooltip, TooltipPosition } from '@tableau/widgets';
import { BodyCell, DataGrid } from '@tableau/widgets-datagrid';
import React from 'react';

import { Messages } from '~src/codegen/Localize';
import { FlowWithRunPermission } from '~src/flow/serverApi/requests/GetFlowsWithRunPermissionsRequest';
import { LinkedTaskCellStyles } from '~src/linkedTasks/styles/LinkedTaskCellStyles';
import { StyledCellFormatter } from '~src/spacesPlatform/components/ListView/StyledCellWrapper';
import { classes } from '~src/style';
import { applyColor } from '~src/style/ColorService';


const handleLinkClick = (e: React.SyntheticEvent<HTMLAnchorElement, Event>) => {
  // navigate to link but don't let handleCellClick get the event
  e.stopPropagation();
};

function renderDisabledInfoToolip() {
  return (
    <Tooltip
      position={TooltipPosition.Right}
      getContent={getTooltipContent}
      maxWidth={220}
    >
      <NotificationInfoOutlineIcon className={LinkedTaskCellStyles.InfoIcon} size={16} color={applyColor(DesignTokens.color.font.default.secondary, '#666666')} alt={''} />
    </Tooltip>
  );
}

function renderFlowCellGoToButton(flowId: string, siteUrls: SiteUrls) {
  return (
    <a
      className={classes(LinkedTaskCellStyles.ContentItem, LinkedTaskCellStyles.FlexShrinkZero, LinkedTaskCellStyles.LinkButton)}
      onClick={handleLinkClick}
      href={siteUrls.flowOverviewUrl(flowId)}
      title={Messages.linkedTasksGoToFlow()}
      rel={'noreferrer'}
      target= {'_blank'}>
        <GotoGenericIcon className={LinkedTaskCellStyles.LinkImg} size={14} color={applyColor(DesignTokens.color.font.default.secondary, '#666666')} alt={''} />
    </a>
  );
}

export function renderPrimaryFlowItem(flowName: string, flowId: string, isDisabled: boolean, siteUrls: SiteUrls) {
  return (
    <div className={LinkedTaskCellStyles.InnerTextWrapper}>
      <FlowBaseIcon className={classes(LinkedTaskCellStyles.ItemIcon, LinkedTaskCellStyles.FlexShrinkZero, isDisabled ? LinkedTaskCellStyles.Disabled : null)} size={18} color={applyColor(DesignTokens.color.font.default.secondary, '#666666')} alt={Messages.flow()} />
      <div className={classes(LinkedTaskCellStyles.ContentItem,  LinkedTaskCellStyles.FlexShrinkZero, isDisabled ? LinkedTaskCellStyles.Disabled : null)}>{Messages.linkedTasksRunFlowSentenceCasing()}</div>
      <div title={flowName} className={classes(LinkedTaskCellStyles.ContentItem, LinkedTaskCellStyles.ContentName, isDisabled ? LinkedTaskCellStyles.Disabled : null)}>{flowName}</div>
      {isDisabled && renderDisabledInfoToolip()}
      {renderFlowCellGoToButton(flowId, siteUrls)}
    </div>
  );
}

const getTooltipContent = () => <span>{Messages.linkedTaskPermissionsInfoTooltip()}</span>;

export function SelectFlowCell(isSelected: (id: string) => boolean, onItemSelectionToggle: (row: DataGrid.Row) => void, siteUrls: SiteUrls): React.StatelessComponent<BodyCell.Props> {
  return StyledCellFormatter(
    function SelectFlowCell(props: BodyCell.Props) {
      const flow: FlowWithRunPermission = props.row.data as FlowWithRunPermission;
      const isDisabled: boolean = !flow.userHasRunPermissions;

      const handleCellClick = (e: React.SyntheticEvent<HTMLDivElement, Event>) => {
        if (!isDisabled) {
          onItemSelectionToggle(props.row);
        }
        e.stopPropagation();
      };

      return (
        /* eslint-disable jsx-a11y/no-static-element-interactions */
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        <div
          onClick={handleCellClick}
          key={flow.id}
          className={classes(LinkedTaskCellStyles.Wrapper)}
          data-tb-test-id={'SelectFlowCellTestId'}
        >
          <div className={LinkedTaskCellStyles.CheckboxWrapper}>
            <CheckBoxWidget
              checked={isSelected(flow.id)}
              testId={'SelectFlowCellCheckboxTestId'}
              removeLeftMargin={true}
              disabled={isDisabled}
            />
          </div>
          <div className={LinkedTaskCellStyles.TextWrapper}>
            {renderPrimaryFlowItem(flow.name, flow.id, isDisabled, siteUrls)}
          </div>
        </div>
      );
    }
  );
}
